import axios from 'axios'
import { Session, Auth, User, Roles } from '../models/Auth'
import { log } from '../helpers/logger'
import Cookies from 'js-cookie';
import { blockToken } from '../requests/BlockedToken';

const params = new URLSearchParams(document.location.search.substring(1))

const AuthService = {
  client: axios.create(),
  redirectToAuthorizationServer: () => {
    const url = `${
      process.env.REACT_APP_AUTH_SERVER_URL
    }/oauth2/authorize?scope=openid&response_type=code&redirect_uri=${encodeURIComponent(
      process.env.REACT_APP_AUTH_REDIRECT_URI ?? '',
    )}&client_id=${process.env.REACT_APP_AUTH_CLIENT_ID}`

    const currentLocation = (document.location.pathname + document.location.search);
    const redirectionUrl = currentLocation != '/' ? `&state=${encodeURIComponent(currentLocation)}` : ''

    log.debug('Auth - redirecting to auth server: ', url)
    AuthService.logout(url + redirectionUrl)
  },

  exhangeGrantCode: () => {
    return new Promise<Auth>((resolve, reject) => {
      var code = params.get('code');
      var bypassEmail = Cookies.get('bypass-email');
      if(AuthService.isAuthOverrideEnabled() && bypassEmail){
        code = `bypass-${bypassEmail ?? ""}`;
      }
      const endpoint = `${process.env.REACT_APP_BASE_API_URL}/sim/v1/getuserinfo/${code}/${
        process.env.REACT_APP_AUTH_ENVIRONMENT
      }`
      log.debug('Auth - Get user info: ', endpoint)
     
      AuthService.client
        // eslint-disable-next-line camelcase
        .get<Auth & { access_token: string; refresh_token: string }>(endpoint)
        .then((response) => {
          log.debug('Auth - Get user info response: ', response.data)
          const authPayload = {
            sub: response.data.sub,
            email: response.data.email,
            accessToken: response.data.access_token,
            refreshToken: response.data.refresh_token,
            roles: response.data.roles,
          }

          AuthService.client.defaults.withCredentials = AuthService.canImpersonate({user:{}, auth:authPayload} as Session);

          resolve(authPayload)
        })
        .catch((error) => {
          reject(error)
        })
        .then(() => {
          const location = new URL(document.location.href)
            location.searchParams.delete("code");
            location.searchParams.delete("session_state");
            window.history.replaceState(window.history.state,"",location.href);
         })
    })
  },

  initialiseAuthFlow: async (
    session: Session,
    onAuthUserCallback: (payload: Session) => void,
    onErrorCallback: () => void,
  ) => {

    log.debug({
      method: "initialiseAuthFlow",
      session,
      params,
      isAuthOverrideEnabled: AuthService.isAuthOverrideEnabled(),
      isImpersonationEnabled:AuthService.isImpersonationEnabled(),
    });

    if (!session?.auth?.sub || !session?.user.UserId) {
      // If we dont have the grant code in the params, redirect the user to Auth server
      if (!params.get('code')) {
        AuthService.redirectToAuthorizationServer()
      } else {
        AuthService.exhangeGrantCode()
        .then((auth) => {
          AuthService.fetchAuthUserByUserId(auth, onAuthUserCallback, onErrorCallback)
        })
        .catch((error) => {
          log.error(error)
          onErrorCallback()
        })
      }
    } else if(AuthService.isAuthBypassRequested(session) || AuthService.isImpersonationRefreshRequired(session)){
      AuthService.fetchAuthUserByUserId(session.auth, onAuthUserCallback, onErrorCallback)
    }
  },

  isAuthBypassRequested(session: Session){
    var bypassEmail = Cookies.get('bypass-email');
    return AuthService.isAuthOverrideEnabled() && bypassEmail && session.user.Email != bypassEmail;
  },

  isImpersonationRefreshRequired(session: Session){
    if(!AuthService.isImpersonationEnabled()){
      return false;
    }

    var impersonateUserEmail = Cookies.get('ImpersonateUserEmail');
    
    return impersonateUserEmail ? 
    (impersonateUserEmail === session?.auth?.email && session.auth.email != session.user.Email) || session.user.Email != impersonateUserEmail
      : session.auth.email !== session.user.Email;
  },

  fetchAuthUserByUserId: (auth: Auth, onSuccessCallback: (payload: Session) => void, onErrorCallback: () => void) => {
    // On first login to SoECAT, extract the currently visited studyId from the parameters and perform an auth request for this study
    // Subsequent auth checks for different studies happen within the `useStudyAuthorisation` hook within App.tsx
    let studyIdParam = ''
    const state = params.get('state')
    if (state) {
      const url = new URL(state, window.location.href)
      const studyId = url.searchParams.get('studyId')
      if (studyId) studyIdParam = `?studyid=${studyId}`
    }
    
    AuthService.client.defaults.withCredentials = AuthService.canImpersonate({user:{}, auth} as Session);

    AuthService.client
      .get<User>(`${process.env.REACT_APP_BASE_API_URL}/sim/v1/authorisation/${auth.sub}${studyIdParam}`)
      .then((response) => {
        log.debug('Auth - fetchAuthUserByUserId response', response)

        const user: User = {
          FirstName: response.data.FirstName,
          LastName: response.data.LastName,
          Email: response.data.Email,
          UserId: response.data.UserId,
          PersonId: response.data.PersonId,
          StudyId: response.data.StudyId,
          StudyRoles: response.data.StudyRoles,
          Roles: response.data.Roles,
        }

        onSuccessCallback({ auth, user, error: undefined })
      })
      .catch((error) => {
        log.error(error)
        onErrorCallback()
      })
      .then(() => {})
  },

  clear: () => {
    window.localStorage.removeItem('session')
  },
  logout: async (url: string) => {
      try {
          const sessionData = localStorage.getItem('session');
          if (!sessionData) {
              console.warn('No session data found in local storage.');
              window.location.href = url;
              return;
          }

          const session = JSON.parse(sessionData);
          const accessToken = session.auth?.accessToken;

          if (!accessToken) {
              console.warn('No accessToken found in session data.');
              window.location.href = url;
              return;
          }
          await blockToken(session.user?.UserId, accessToken);

      } catch (error) {
          console.error('Error adding token to block list:', error);
      }

      AuthService.clear();
      window.location.href = url;
  },
  canImpersonate: (session: Session) => {
    return AuthService.isImpersonationEnabled()
      && session?.auth?.roles?.includes(Roles.Tester);
  },
  isImpersonating: (session: Session): boolean =>{
    var impersonateUserEmail = Cookies.get('ImpersonateUserEmail');
    return AuthService.canImpersonate(session) && Boolean(impersonateUserEmail) && Boolean(session?.auth?.sub) && Boolean(session?.user?.UserId) && session.auth.sub != session.user.UserId;
  },
  isImpersonationEnabled: () => {
    return (process.env.REACT_APP_ENABLE_IMPERSONATION ?? 'false')=='true';
  },
  isAuthOverrideEnabled: () => {
    return (process.env.REACT_APP_OVERRIDE_AUTH ?? 'false')=='true';
  }
}

export default AuthService
